import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAdminAuth } from "@/lib/adminAuth";

export interface User {
  id: number;
  email: string;
  username?: string;
  role?: string;
  isAdmin: boolean;
  type: 'admin' | 'client';
  leadId?: number;
  totpEnabled?: boolean;
}

export interface LoginResult {
  success: boolean;
  requiresTwoFactor?: boolean;
  tempToken?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  setAuthToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = "auth_token";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);

  // Keep ref in sync with state
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const refreshUser = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!currentToken) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch("/api/me", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${currentToken}`,
        },
      });

      if (!res.ok) {
        // Token invalid/expired
        try {
          localStorage.removeItem(TOKEN_KEY);
        } catch {}
        setToken(null);
        setUser(null);
        return;
      }

      const data = await res.json();
      setUser({
        id: data.id,
        email: data.email,
        username: data.username,
        role: data.role,
        isAdmin: data.isAdmin || false,
        type: data.type || 'client',
        leadId: data.leadId,
        totpEnabled: data.totpEnabled,
      });
    } catch (error) {
      console.error("Failed to refresh user:", error);
      try {
        localStorage.removeItem(TOKEN_KEY);
      } catch {}
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setToken]);

  const setAuthToken = useCallback((newToken: string) => {
    try {
      localStorage.setItem(TOKEN_KEY, newToken);
    } catch {}
    setToken(newToken);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const res = await fetch("/api/admin/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error?.error || error?.message || "Login failed");
    }

    const data = await res.json();
    
    // Return 2FA requirement to caller
    if (data.requiresTwoFactor) {
      return { success: false, requiresTwoFactor: true, tempToken: data.tempToken };
    }

    const newToken = data.token;
    if (!newToken) {
      throw new Error("Login failed: missing token");
    }

    setAuthToken(newToken);
    
    // Also sync with Zustand admin auth store for adminFetch to use
    if (data.admin) {
      useAdminAuth.getState().setAuth(newToken, data.refreshToken || "", {
        id: data.admin.id,
        email: data.admin.email,
        username: data.admin.username || data.admin.email.split('@')[0],
        role: data.admin.role || 'admin',
        totpEnabled: data.admin.totpEnabled || false,
      });
    }
    
    // Fetch user info immediately after login
    const userRes = await fetch("/api/me", {
      headers: {
        "Authorization": `Bearer ${newToken}`,
      },
    });
    
    if (userRes.ok) {
      const userData = await userRes.json();
      setUser({
        id: userData.id,
        email: userData.email,
        username: userData.username,
        role: userData.role,
        isAdmin: userData.isAdmin || false,
        type: userData.type || 'admin',
        leadId: userData.leadId,
        totpEnabled: userData.totpEnabled,
      });
    }
    
    return { success: true };
  }, [setAuthToken]);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {}
    setToken(null);
    setUser(null);
    // Also logout from Zustand admin auth store
    useAdminAuth.getState().logout();
  }, []);

  // Only run refreshUser when token changes, not when refreshUser changes
  useEffect(() => {
    refreshUser();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: AuthContextType = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    isAdmin: user?.isAdmin || false,
    login,
    logout,
    refreshUser,
    setAuthToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
